/**
 * PublicLayout — layout commun à toutes les pages publiques (landing, login,
 * inscription, forgot/reset/verify, pages légales).
 *
 * Refonte R3 (mai 2026) :
 *   - Topbar en glass avec halo radial tech localisé
 *   - LogoMark composant R2 (gradient + glow tech)
 *   - Footer en glass discret avec border-t subtle
 *   - Background page : halos radiaux automatiques (cf. R1 globals.css)
 *
 * API publique inchangée : children, maxWidth, centered.
 * Accessibilité : skip link déjà géré par App.tsx, <main id="main-content">.
 */

import { Link } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { VpcMairieIcon } from './ui/VpcMairieIcon';
import { ThemeToggle } from './ThemeToggle';
import { LogoMark, SectionEyebrow } from './ui';
import type { ReactNode } from 'react';

interface PublicLayoutProps {
  children: ReactNode;
  /** Titre de page (RGAA 2.4.2). */
  titre?: string;
  /** Largeur max du conteneur central (Tailwind class). */
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl';
  /** Centrage vertical du contenu. */
  centered?: boolean;
}

const maxWidthMap = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-3xl',
  '2xl': 'max-w-4xl',
  '3xl': 'max-w-5xl',
  '4xl': 'max-w-6xl',
} as const;

export function PublicLayout({ children, maxWidth = 'md', centered = true, titre }: PublicLayoutProps) {
  usePageTitle(titre);
  return (
    <div className="flex min-h-screen flex-col text-ink">
      {/* ===== Topbar glass avec halo radial tech ===== */}
      <header
        className="sticky top-0 z-20 flex items-center gap-3 border-b border-border px-4 py-3 backdrop-blur-glass sm:px-6"
        style={{
          backgroundImage:
            'radial-gradient(220px 80px at 10% 50%, oklch(var(--c-tech) / 0.22), transparent 70%), ' +
            'linear-gradient(90deg, oklch(var(--c-bg-2) / 0.65), oklch(var(--c-bg) / 0.45))',
        }}
      >
        <Link
          to="/"
          className="flex items-center gap-2.5 rounded-r-lg px-1 py-1 transition-all hover:bg-surface/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
          aria-label="Accueil Vision Pro Collectivité"
        >
          <LogoMark icon={VpcMairieIcon} accent="tech" size="md" />
          <div className="hidden sm:block">
            <SectionEyebrow>One For All Distribution</SectionEyebrow>
            <div className="font-display text-sm font-semibold tracking-r-snug text-ink">
              Vision Pro Collectivité
            </div>
          </div>
          {/* Sur mobile, on n'affiche que le LogoMark */}
          <span className="font-display text-sm font-semibold tracking-r-snug text-ink sm:hidden">
            VPC
          </span>
        </Link>

        <div className="ml-auto flex items-center gap-2">
          <ThemeToggle />
        </div>
      </header>

      {/* ===== Main ===== */}
      <main
        id="main-content"
        className={`flex flex-1 flex-col ${centered ? 'items-center justify-center' : ''} px-4 py-8 sm:py-12`}
      >
        <div className={`w-full ${maxWidthMap[maxWidth]} animate-fade-in`}>{children}</div>
      </main>

      {/* ===== Footer glass discret ===== */}
      <footer
        className="border-t border-border px-4 py-5 text-center text-xs text-ink-subtle backdrop-blur-glass"
        style={{
          backgroundImage:
            'linear-gradient(180deg, transparent, oklch(var(--c-bg-2) / 0.4))',
        }}
      >
        <div className="mx-auto max-w-4xl space-y-2">
          <nav
            aria-label="Pages légales"
            className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2"
          >
            <Link
              to="/mentions-legales"
              className="rounded-r-md px-1 py-0.5 hover:text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
            >
              Mentions légales
            </Link>
            <span aria-hidden="true" className="text-ink-dim">·</span>
            <Link
              to="/politique-confidentialite"
              className="rounded-r-md px-1 py-0.5 hover:text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
            >
              Politique de confidentialité
            </Link>
            <span aria-hidden="true" className="text-ink-dim">·</span>
            <Link
              to="/cgu"
              className="rounded-r-md px-1 py-0.5 hover:text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
            >
              CGU
            </Link>
            <span aria-hidden="true" className="text-ink-dim">·</span>
            {/* CGV — conditions de l'abonnement souscrit par la collectivité. */}
            <Link
              to="/cgv"
              className="rounded-r-md px-1 py-0.5 hover:text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
            >
              CGV
            </Link>
            <span aria-hidden="true" className="text-ink-dim">·</span>
            <Link
              to="/accessibilite"
              className="rounded-r-md px-1 py-0.5 hover:text-tech-fg hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-tech"
            >
              Accessibilité
            </Link>
          </nav>
          <p className="text-ink-dim">
            Vision Pro Collectivité — édité par <strong className="text-ink-subtle">One For All Distribution</strong>
            {' · '}hébergement souverain UE{' · '}RGPD conforme
          </p>
        </div>
      </footer>
    </div>
  );
}
